import { uuid } from '../types/types';
import { create } from 'zustand';
import { createUuid } from '../utilities';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import type {} from '@redux-devtools/extension';
import type { KV } from '../types/types';
import { indexedDbStorage } from '../utilities/indexedDbStorage';

// Each `List` has its own state, and contains multiple `Item`s
export type List = {
  id: uuid;
  keys?: KV; // a map of strings, internal usage, represents the meaning of this list.
  label?: string; // label will be changed/derived from the date, like Today, Yesterday, Tomorrow, etc.
  icon?: string; // icon like ☀️, 🌙, etc.
  iconMutable?: boolean; // if true, the icon can be changed by user
  createdDate?: string; // Date converted toISOString as local storage can not hold Object
  updatedDate?: string; // Date converted toISOString as local storage can not hold Object
};

export type ListMap = Record<uuid, List>; // listId -> list state
export type ItemsMap = Record<uuid, uuid[]>; // listId -> task item id []

export type Page = {
  id: uuid;
  name: string;
  listIds: uuid[];
};

type ListStore = {
  // state
  itemsMap: ItemsMap;
  userListMap: ListMap;
  taskListMap: ListMap;
  visibleUserListIds: uuid[];
  pages: Page[];
  activePageId: uuid;

  reset: () => void;

  setItemsMap: (itemsMap: ItemsMap) => void;
  updateItemsMap: (updater: (currentMap: ItemsMap) => ItemsMap) => void; // callback style

  updateVisibleUserListIds: (listIds: uuid[]) => void;

  addUserList: (label: string, key: KV) => uuid;
  removeUserList: (id: uuid) => void;
  removeItemFromItemsMap: (id: uuid, itemId: uuid) => void;

  updateIcon: (id: uuid, icon: string) => void;
  updateLabel: (id: uuid, label: string) => void;
  updateListDate: (
    id: uuid,
    updatedDate: Date,
    overwriteCreatedDate?: Date
  ) => void;

  addPage: () => void;
  setActivePageId: (id: uuid) => void;
};

const IMPORTANT_URGENT_LIST_ID = '223270cb-72e5-43be-959c-d4e1744a96a6';
const URGENT_LIST_ID = '71c4cdf4-0ff3-4722-8069-47a2bb9dd3de';
const IMPORTANT_LIST_ID = 'dc79a944-2a0a-49e5-aa38-19cbca74aabe';
const LOW_PRIORITY_LIST_ID = 'c3565f9e-50e4-4a96-966b-ba95ea2c3292';
export const TRASH_LIST_ID = '2570075d-318e-4a6c-9410-6c9f6a7d1a55';

// These 5 list ids are reserved and immutable.
const defaultItemsMap: ItemsMap = {
  [IMPORTANT_URGENT_LIST_ID]: [],
  [URGENT_LIST_ID]: [],
  [IMPORTANT_LIST_ID]: [],
  [LOW_PRIORITY_LIST_ID]: [],
  [TRASH_LIST_ID]: [],
};

/// System default lists
export const EM_ = 'EM_';

export enum PRIORITY {
  EM_IMP_URG = `${EM_}IMP_URG`,
  EM_URG = `${EM_}URG`,
  EM_IMP = `${EM_}IMP`,
  EM_LOW = `${EM_}LOW`,
}

/// Day Key starts with this prefix
export const DAY_ = 'DAY_';

/// In order to get them updated when new version JS is launched.
export const defaultSystemListMap: ListMap = {
  [IMPORTANT_URGENT_LIST_ID]: {
    id: IMPORTANT_URGENT_LIST_ID,
    keys: { priority: PRIORITY.EM_IMP_URG },
    label: 'Important',
    icon: '🌱',
    iconMutable: true,
  },
  [URGENT_LIST_ID]: {
    id: URGENT_LIST_ID,
    keys: { priority: PRIORITY.EM_URG },
    label: 'Urgent',
    icon: '⚡',
    iconMutable: true,
  },
  [LOW_PRIORITY_LIST_ID]: {
    id: LOW_PRIORITY_LIST_ID,
    keys: { priority: PRIORITY.EM_LOW },
    label: 'Inbox',
    icon: '📥',
    iconMutable: true,
  },
};

export const getListIds = (itemsMap: ListMap): uuid[] => {
  return Object.keys(itemsMap);
};

const DEFAULT_PAGE_ID = 'default-page-id';

const initState = {
  itemsMap: { ...defaultItemsMap },
  userListMap: {
    [IMPORTANT_URGENT_LIST_ID]: {
      ...defaultSystemListMap[IMPORTANT_URGENT_LIST_ID],
    },
    [URGENT_LIST_ID]: {
      ...defaultSystemListMap[URGENT_LIST_ID],
    },
    [LOW_PRIORITY_LIST_ID]: {
      ...defaultSystemListMap[LOW_PRIORITY_LIST_ID],
    },
  },
  taskListMap: {
    [TRASH_LIST_ID]: {
      id: TRASH_LIST_ID,
      label: 'Trash',
      icon: '🗑️',
      iconMutable: false,
    },
  },

  // which user lists are visible in diary sidebar
  visibleUserListIds: [],
  pages: [
    {
      id: DEFAULT_PAGE_ID,
      name: 'Page 1',
      listIds: [IMPORTANT_URGENT_LIST_ID, URGENT_LIST_ID, LOW_PRIORITY_LIST_ID],
    },
  ],
  activePageId: DEFAULT_PAGE_ID,
};

export const useTaskListStore = create<ListStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initState,

        reset: () => {
          console.warn('Attention: reset');
          set(initState);
        },

        addUserList: (label: string, key: KV): uuid => {
          let newId = createUuid();
          const time = new Date().toISOString();
          set((state) => {
            // Check if the key already exists in the userListMap
            const keyExists = Object.values(state.userListMap).find((list) =>
              Object.keys(list?.keys || {}).some(
                (k) => list?.keys?.[k] === key?.[k]
              )
            );

            // If the date does not exist, create a new list
            if (!keyExists) {
              const updatedPages = state.pages
                ? state.pages.map((page) => {
                    if (page.id === state.activePageId) {
                      return {
                        ...page,
                        listIds: [...page.listIds, newId],
                      };
                    }
                    return page;
                  })
                : [];

              return {
                ...state,
                userListMap: {
                  ...state.userListMap,
                  [newId]: {
                    id: newId,
                    keys: key,
                    label: label,
                    icon: '😶',
                    iconMutable: true,
                    createdDate: time,
                    updatedDate: time,
                  },
                },
                itemsMap: {
                  ...state.itemsMap,
                  [newId]: [],
                },
                pages: updatedPages,
              };
            } else {
              newId = keyExists.id;
              return state;
            }
          });
          return newId;
        },

        updateVisibleUserListIds: (listIds) =>
          set({ visibleUserListIds: listIds }),

        setItemsMap: (listItemsMap) => set({ itemsMap: listItemsMap }),
        updateItemsMap: (updater) =>
          set((state) => ({
            itemsMap: updater(state.itemsMap),
          })),

        removeUserList: (id) => {
          set((state) => {
            const { [id]: _, ...restUserListMap } = state.userListMap;
            const { [id]: __, ...restItemsMap } = state.itemsMap;
            const updatedVisibleIds = state.visibleUserListIds.filter(
              (listId) => listId !== id
            );
            const updatedPages = state.pages
              ? state.pages.map((page) => ({
                  ...page,
                  listIds: page.listIds.filter((listId) => listId !== id),
                }))
              : [];

            return {
              userListMap: restUserListMap,
              itemsMap: restItemsMap,
              visibleUserListIds: updatedVisibleIds,
              pages: updatedPages,
            };
          });
        },

        removeItemFromItemsMap: (containerId: uuid, itemId: uuid) => {
          set((state) => {
            // Find the container in itemsMap, and filter out the itemId
            const updatedItemsMap = { ...state.itemsMap };
            const container = updatedItemsMap[containerId];
            if (container) {
              updatedItemsMap[containerId] = container.filter(
                (id) => id !== itemId
              );
            }
            return { itemsMap: updatedItemsMap };
          });
        },

        updateIcon: (id, icon) =>
          set((state) => {
            const updatedUserLists = { ...state.userListMap };
            if (updatedUserLists[id]) {
              updatedUserLists[id] = { ...updatedUserLists[id], icon };
            }
            const updatedTaskLists = { ...state.taskListMap };
            if (updatedTaskLists[id]) {
              updatedTaskLists[id] = { ...updatedTaskLists[id], icon };
            }
            return {
              userListMap: updatedUserLists,
              taskListMap: updatedTaskLists,
            };
          }),

        updateLabel: (id, label) =>
          set((state) => {
            const updatedUserLists = { ...state.userListMap };
            if (updatedUserLists[id]) {
              updatedUserLists[id] = { ...updatedUserLists[id], label };
            }
            const updatedTaskLists = { ...state.taskListMap };
            if (updatedTaskLists[id]) {
              updatedTaskLists[id] = { ...updatedTaskLists[id], label };
            }
            return {
              userListMap: updatedUserLists,
              taskListMap: updatedTaskLists,
            };
          }),

        updateListDate: (id, updatedDate, overwriteCreatedDate) =>
          set((state) => {
            const updatedLists = { ...state.userListMap };
            const targetList = updatedLists[id];
            if (targetList) {
              updatedLists[id] = {
                ...targetList,
                updatedDate: updatedDate.toISOString(),
                createdDate:
                  overwriteCreatedDate?.toISOString() || targetList.createdDate,
              };
            }
            return { userListMap: updatedLists };
          }),

        setActivePageId: (id) => set({ activePageId: id }),

        addPage: () => {
          const newPageId = createUuid();
          const impId = createUuid();
          const urgId = createUuid();
          const inboxId = createUuid();
          const time = new Date().toISOString();

          set((state) => {
            const newPageNum = (state.pages?.length || 0) + 1;
            const newPageName = `Page ${newPageNum}`;

            const newPage: Page = {
              id: newPageId,
              name: newPageName,
              listIds: [impId, urgId, inboxId],
            };

            return {
              ...state,
              pages: [...(state.pages || []), newPage],
              activePageId: newPageId,
              userListMap: {
                ...state.userListMap,
                [impId]: {
                  id: impId,
                  keys: { priority: PRIORITY.EM_IMP_URG },
                  label: 'Important',
                  icon: '🌱',
                  iconMutable: true,
                  createdDate: time,
                  updatedDate: time,
                },
                [urgId]: {
                  id: urgId,
                  keys: { priority: PRIORITY.EM_URG },
                  label: 'Urgent',
                  icon: '⚡',
                  iconMutable: true,
                  createdDate: time,
                  updatedDate: time,
                },
                [inboxId]: {
                  id: inboxId,
                  keys: { priority: PRIORITY.EM_LOW },
                  label: 'Inbox',
                  icon: '📥',
                  iconMutable: true,
                  createdDate: time,
                  updatedDate: time,
                },
              },
              itemsMap: {
                ...state.itemsMap,
                [impId]: [],
                [urgId]: [],
                [inboxId]: [],
              },
            };
          });
        },
      }),
      {
        name: 'task-list-storage',
        storage: createJSONStorage(() => indexedDbStorage),
      }
    )
  )
);
